import { useMemo } from 'react';
import { FORM } from '../graphql';
import useEnhancedQuery from './useEnhancedQuery';

// The company form list is fetched with name_only, which leaves out origin.
// Fetch origin only for the forms referenced in the activity log, so the
// actor label can show the channel type ("AndaBreeze - Website").
const LIST_FORM_ORIGINS = FORM.LIST({
  fields: '{ forms { id name origin fb_form_id } count }',
  fragments: [],
});

const getActivityFormIDs = (activities, extraFormIDs) => {
  const ids = new Set((extraFormIDs || []).filter(Boolean));
  (activities || []).forEach((activity) => {
    const formID = activity && activity.new_values && activity.new_values.form_id;
    if (formID) {
      ids.add(formID);
    }
  });
  return [...ids].sort();
};

/**
 * Adds each form's origin to formList for the forms used in the activities
 * (plus extraFormIDs, e.g. the form the lead came from).
 * Falls back to formList unchanged when the lookup fails or is still loading.
 */
const useActivityFormList = ({
  activities, formList, client, extraFormIDs,
}) => {
  const extraKey = (extraFormIDs || []).join(',');
  const formIDs = useMemo(
    () => getActivityFormIDs(activities, extraKey ? extraKey.split(',') : []),
    [activities, extraKey],
  );

  const query = useEnhancedQuery(LIST_FORM_ORIGINS, {
    options: {
      client,
      skip: formIDs.length === 0,
      variables: {
        fields: ['id', 'name', 'origin', 'fb_form_id'],
        filters: formIDs.map((id) => ({ field: 'id', operator: '==', value: id })),
        limit: formIDs.length,
      },
    },
    // Label falls back to the form name, so a failed lookup should not interrupt the page
    disableDefaultError: true,
    selector: (d) => (d.listForms && d.listForms.forms) || [],
  });

  const originForms = query.data;
  return useMemo(() => {
    if (!Array.isArray(originForms) || originForms.length === 0) {
      return formList || [];
    }
    const byID = new Map(originForms.map((f) => [f.id, f]));
    return (formList || []).map((form) => {
      const detail = byID.get(form.id);
      return detail ? { ...form, origin: detail.origin } : form;
    });
  }, [formList, originForms]);
};

export default useActivityFormList;
